import React, { useCallback, useState, useEffect } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { useQueryClient } from '@tanstack/react-query';
import { Person } from 'talent_matching/types';
import { useUpdatePerson } from 'talent_matching/hooks/use_people';

const t = withLocalePrefix('talent_matching.tags');

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  getTagsPath: string;
  addTagsPath: string;
  fullName: string;
  currentTags: Item[];
  application: Person;
  canCreateTags: boolean;
};

const AddTagsModal = ({
  isOpen,
  closeModal,
  getTagsPath,
  addTagsPath,
  fullName,
  currentTags,
  application,
  canCreateTags,
}: Props) => {
  const [selectedTags, setSelectedTags] = useState<Item[]>(currentTags);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const updatePerson = useUpdatePerson();

  useEffect(() => {
    setSelectedTags([...currentTags]);
  }, [isOpen, currentTags]);

  const fetchItems = useCallback(
    async (term: string) => {
      const queryKey = ['tags', term];

      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: cachedData };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(
            getTagsPath,
            { params: { term } }
          );
          return response.data.results;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
      });

      return { items };
    },
    [getTagsPath, queryClient]
  );

  const dropdownProps = useMultiDropdown({
    fetchItems,
    paginated: false,
    selectedItems: selectedTags,
    onSelectedItemsChange: (changes) => setSelectedTags(changes.selectedItems),
    creatable: canCreateTags,
    preventLoadOnMount: false,
  });

  const onSubmit = async () => {
    setSubmitting(true);

    try {
      const res = await standardRequestWithErrorFlash.put(
        addTagsPath,
        { tags: selectedTags },
        {
          successMessage: t('success'),
        }
      );

      if (res?.status === 200) {
        closeModal();
        setSubmitting(false);
        const updatedApplication = {
          ...application,
          tags: selectedTags,
        };
        updatePerson(updatedApplication);
      }
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={closeModal}
      ariaLabel={t('title')}
      size="medium"
    >
      <DataEntryModal.Header onClick={closeModal}>
        <span className="pii-blur">{t('title', { name: fullName })}</span>
      </DataEntryModal.Header>

      <DataEntryModal.Body>
        <FullWidth>
          <div className="pii-blur">
            <MultiDropdown dropdownProps={dropdownProps}>
              <MultiDropdown.Field label={t('label')} />
              <MultiDropdown.Flyout />
            </MultiDropdown>
          </div>
        </FullWidth>
      </DataEntryModal.Body>

      <DataEntryModal.Footer>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <SecondaryButton onClick={closeModal} disabled={submitting}>
            {t('cancel')}
          </SecondaryButton>
          <PrimaryButton
            onClick={onSubmit}
            disabled={submitting || selectedTags.length === 0}
          >
            {t('save')}
          </PrimaryButton>
        </Stack>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

export default AddTagsModal;
